import { ESearchFilters } from '@/constants/searchFilters';
import { vehicleStyleToTypeMap } from '@/constants/vehicleDetails';

/**
 * Shape returned by the agent platform's RV AI-search extractor
 * (`POST /api/rv/search` → `response.filters`). The HTTP contract is the
 * boundary between the two repos — keep this in sync with
 * agents-platform/src/schemas/rv-search-filters.ts (rvSearchFiltersSchema).
 * All fields optional; the extractor emits only what the renter expressed.
 */
export interface IAiSearchFilters {
  address?: string;
  dateFrom?: string;
  dateTo?: string;
  sleepsAdults?: number;
  sleepsChildren?: number;
  /** Minimum seatbelts — seated capacity while driving (distinct from sleeping). */
  seatbelts?: number;
  /** Outdoorsy RV class slugs: a, b, c, camper-van, trailer, fifth-wheel, ... */
  vehicleType?: string[];
  /** Broad category when no specific class was named — expands via vehicleStyleToTypeMap. */
  style?: 'drive' | 'tow';
  priceMin?: number;
  priceMax?: number;
  lengthMax?: number;
  features?: string[];
  petFriendly?: boolean;
  delivery?: boolean;
  deliveryStationary?: boolean;
  instantBook?: boolean;
  unlimitedMiles?: boolean;
  unlimitedGenerator?: boolean;
  make?: string[];
  /** Free-text model(s), pairs with make. Maps to filter[vehicle_model]. */
  model?: string[];
  yearMin?: number;
  yearMax?: number;
  minRating?: number;
  nearby?: boolean;
  /** Host type(s): 'superhost' / 'military' → filter[tags-slug]; 'pro' → filter[ownerType]=dealer. */
  hostType?: string[];
  /** Flexible/free cancellation → cancelPolicy=flexible. */
  flexibleCancellation?: boolean;
  /** Max tow weight in lbs (towables) → trailerWeight[lte]. */
  towWeightMax?: number;
  /** Free-text keyword catch-all → filter[keywords]. */
  keywords?: string;
  /**
   * Qualitative feel of the trip, matched against each rental's
   * vibe_spotlight.primary_vibe (family, luxury, comfort, beginner,
   * adventure). Soft: never sent as a URL filter.
   */
  vibes?: string[];
  /** Listing tag slugs the renter would like (e.g. good-for-couples). Soft: never sent as a URL filter. */
  tags?: string[];
  /** Listing tag slugs the renter said are required ("must be pet friendly"). Sent as filter[tags-slug]. */
  requiredTags?: string[];
}

type TSearchParamMap = Partial<Record<ESearchFilters, string>>;

/**
 * Translate the extractor's structured filters into the `ESearchFilters`
 * URL-param map that `applySearchFilter` consumes. Pure and total — no
 * side effects, every field independently optional. Price→total_price
 * conversion (when dates are present) is handled downstream by
 * getSearchResults, so we always emit price[min]/price[max] here.
 */
export function aiSearchToParams(filters: IAiSearchFilters): TSearchParamMap {
  const params: TSearchParamMap = {};

  // "near me" wins over a named address — omit address and let the
  // backend's geo ranking take over (same as the widget's near-me path).
  if (filters.address && !filters.nearby) {
    params[ESearchFilters.ADDRESS] = filters.address;
  }

  if (filters.dateFrom) params[ESearchFilters.DATE_FROM] = filters.dateFrom;
  if (filters.dateTo) params[ESearchFilters.DATE_TO] = filters.dateTo;

  if (filters.sleepsAdults != null) {
    params[ESearchFilters.SLEEPS_ADULTS] = String(filters.sleepsAdults);
  }
  if (filters.sleepsChildren != null) {
    params[ESearchFilters.SLEEPS_CHILDREN] = String(filters.sleepsChildren);
  }
  if (filters.seatbelts != null) params[ESearchFilters.SEATBELTS] = String(filters.seatbelts);

  // Vehicle classes: explicit classes ∪ the set a drive/tow style expands to.
  const types = new Set<string>(filters.vehicleType ?? []);
  if (filters.style) {
    const expanded = vehicleStyleToTypeMap[filters.style === 'drive' ? 'drivable' : 'towable'];
    expanded.forEach(t => types.add(t));
  }
  if (types.size > 0) params[ESearchFilters.FILTER_TYPE] = Array.from(types).join(',');

  // price[min]/price[max] are stored in CENTS (the price filter multiplies the
  // dollar input by 100), so a $400 cap is 40000 — not 400, which reads as $4.
  if (filters.priceMin != null) params[ESearchFilters.PRICE_MIN] = String(filters.priceMin * 100);
  if (filters.priceMax != null) params[ESearchFilters.PRICE_MAX] = String(filters.priceMax * 100);

  if (filters.lengthMax != null) {
    params[ESearchFilters.LENGTH_BETWEEN] = `0-${filters.lengthMax}`;
  }

  // Amenities ∪ pet_friendly (pet-friendly is just a feature flag on the backend).
  const features = new Set<string>(filters.features ?? []);
  if (filters.petFriendly) features.add('pet_friendly');
  if (features.size > 0) params[ESearchFilters.FILTER_FEATURE] = Array.from(features).join(',');

  if (filters.deliveryStationary) {
    // Stationary implies delivery; the param value is the literal 'stationary'.
    params[ESearchFilters.DELIVERY] = 'true';
    params[ESearchFilters.DELIVERY_STATIONARY] = 'stationary';
  } else if (filters.delivery) {
    params[ESearchFilters.DELIVERY] = 'true';
  }

  if (filters.instantBook) params[ESearchFilters.INSTANT_BOOK] = 'true';
  if (filters.unlimitedMiles) params[ESearchFilters.UNLIMITED_MILES] = 'true';
  if (filters.unlimitedGenerator) params[ESearchFilters.UNLIMITED_GENERATOR] = 'true';

  if (filters.make?.length) params[ESearchFilters.FILTER_VEHICLE_MAKE] = filters.make.join(',');
  if (filters.model?.length) {
    params[ESearchFilters.FILTER_VEHICLE_MAKE_MODELS] = filters.model.join(',');
  }

  if (filters.yearMin != null) params[ESearchFilters.YEAR_GREATER] = String(filters.yearMin);
  if (filters.yearMax != null) params[ESearchFilters.YEAR_LESSER] = String(filters.yearMax);

  if (filters.minRating != null) params[ESearchFilters.SCORE] = String(filters.minRating);

  // Only required tags filter. Soft vibes/tags would hide most inventory as
  // hard filters, and search has no way to rank by them yet. A required tag
  // that a concrete filter already covers is skipped: the pet-friendly tag is
  // on far fewer listings than the pet_friendly feature, so sending both
  // empties the results.
  // filter[tags-slug] carries both required tags and host-type tags
  // (superhost, military-host); pro maps to filter[ownerType] instead.
  const tagsSlug = new Set<string>(
    (filters.requiredTags ?? []).filter(
      tag => !(tag === 'pet-friendly' && features.has('pet_friendly')),
    ),
  );
  (filters.hostType ?? []).forEach(h => {
    if (h === 'superhost') tagsSlug.add('superhost');
    else if (h === 'military') tagsSlug.add('military-host');
    else if (h === 'pro') params[ESearchFilters.FILTER_OWNERTYPE] = 'dealer';
  });
  if (tagsSlug.size > 0) params[ESearchFilters.FILTER_TAGS_SLUG] = Array.from(tagsSlug).join(',');

  if (filters.flexibleCancellation) params[ESearchFilters.CANCEL_POLICY] = 'flexible';
  if (filters.towWeightMax != null) {
    params[ESearchFilters.WEIGHT_LESSER] = String(filters.towWeightMax);
  }
  if (filters.keywords) params[ESearchFilters.FILTER_KEYWORDS] = filters.keywords;

  return params;
}

/**
 * Facet field name (from the extractor's `remove` array) → the ESearchFilters
 * URL params to clear. petFriendly/features are handled separately below since
 * they live inside the comma-joined filter[feature] value.
 */
const REMOVE_PARAM_MAP: Partial<Record<keyof IAiSearchFilters, ESearchFilters[]>> = {
  address: [ESearchFilters.ADDRESS],
  dateFrom: [ESearchFilters.DATE_FROM],
  dateTo: [ESearchFilters.DATE_TO],
  sleepsAdults: [ESearchFilters.SLEEPS_ADULTS],
  sleepsChildren: [ESearchFilters.SLEEPS_CHILDREN],
  seatbelts: [ESearchFilters.SEATBELTS],
  vehicleType: [ESearchFilters.FILTER_TYPE],
  style: [ESearchFilters.FILTER_TYPE],
  priceMin: [ESearchFilters.PRICE_MIN],
  priceMax: [ESearchFilters.PRICE_MAX],
  lengthMax: [ESearchFilters.LENGTH_BETWEEN],
  delivery: [ESearchFilters.DELIVERY, ESearchFilters.DELIVERY_STATIONARY],
  deliveryStationary: [ESearchFilters.DELIVERY_STATIONARY],
  instantBook: [ESearchFilters.INSTANT_BOOK],
  unlimitedMiles: [ESearchFilters.UNLIMITED_MILES],
  unlimitedGenerator: [ESearchFilters.UNLIMITED_GENERATOR],
  make: [ESearchFilters.FILTER_VEHICLE_MAKE],
  model: [ESearchFilters.FILTER_VEHICLE_MAKE_MODELS],
  yearMin: [ESearchFilters.YEAR_GREATER],
  yearMax: [ESearchFilters.YEAR_LESSER],
  minRating: [ESearchFilters.SCORE],
  flexibleCancellation: [ESearchFilters.CANCEL_POLICY],
  towWeightMax: [ESearchFilters.WEIGHT_LESSER],
  keywords: [ESearchFilters.FILTER_KEYWORDS],
  // hostType is handled specially below (tags-slug is shared with requiredTags).
};

/**
 * Build the param map that CLEARS the facets named in the extractor's `remove`
 * array. Empty-string values signal removal — applySearchFilter deletes any
 * param whose value is falsy. petFriendly removes just `pet_friendly` from the
 * existing filter[feature] list (keeping other amenities); `features` clears
 * the whole list. `currentParams` is the live query state (for the feature list).
 */
export function buildRemovalParams(
  remove: string[],
  currentParams: Record<string, string | string[] | undefined>,
): TSearchParamMap {
  const out: TSearchParamMap = {};

  // Current amenity list, read lazily and mutated as pet/features removals apply.
  let features: string[] | null = null;
  const currentFeatures = (): string[] => {
    if (features === null) {
      const raw = currentParams?.[ESearchFilters.FILTER_FEATURE];
      const str = Array.isArray(raw) ? raw[0] : raw;
      features = typeof str === 'string' && str ? str.split(',').filter(Boolean) : [];
    }
    return features;
  };

  for (const field of remove) {
    if (field === 'petFriendly') {
      features = currentFeatures().filter(f => f !== 'pet_friendly');
      out[ESearchFilters.FILTER_FEATURE] = features.length ? features.join(',') : '';
    } else if (field === 'features') {
      features = [];
      out[ESearchFilters.FILTER_FEATURE] = '';
    } else if (field === 'hostType') {
      // tags-slug is shared with requiredTags — drop only the host tags, keep
      // the rest; clear ownerType (pro) outright.
      const raw = currentParams?.[ESearchFilters.FILTER_TAGS_SLUG];
      const str = Array.isArray(raw) ? raw[0] : raw;
      const tags = (typeof str === 'string' && str ? str.split(',').filter(Boolean) : []).filter(
        t => t !== 'superhost' && t !== 'military-host',
      );
      out[ESearchFilters.FILTER_TAGS_SLUG] = tags.length ? tags.join(',') : '';
      out[ESearchFilters.FILTER_OWNERTYPE] = '';
    } else {
      const keys = REMOVE_PARAM_MAP[field as keyof IAiSearchFilters];
      if (keys) keys.forEach(k => (out[k] = ''));
    }
  }

  return out;
}
